import React, { useState, useMemo } from 'react';
import { Card, Button, Badge, OverlayTrigger, Tooltip, Row, Col } from 'react-bootstrap';
import { motion, AnimatePresence } from 'framer-motion';
import DEJEUNER from "../../../../assets/images/diet/dejeuner1.png";
import { GetDailyDataDashUser } from "../../../../core/store/data-dashboard/thunk";
import { 
  CheckCircle, 
  ChevronDown, 
  ChevronUp, 
  Check, 
  Utensils, 
  Zap, 
  Clock as ClockIcon,
  AlertCircle,
  Droplet,
  Activity
} from 'lucide-react';
import { useDispatch } from "react-redux";
import { toast } from 'react-toastify';
import styled from 'styled-components';
import moment from 'moment';

// Composants stylisés
const StyledCard = styled(Card)`
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  }
`;

const MealHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  cursor: pointer;
  background-color: #fff;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;

  &:hover {
    background-color: #f8f9fa;
  }
`;

const MealTitle = styled.h5`
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  font-weight: 500;
  color: #2c3e50;
`;

const NutritionBadge = styled(Badge)`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 10px;
  border-radius: 20px;
  font-weight: 500;
  background-color: #f8f9fa;
  color: #495057;
  border: 1px solid #e9ecef;
`;

const MealImage = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: ${props => props.$hasImage ? `url(${props.$imageUrl})` : '#f8f9fa'};
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  
  .meal-icon {
    font-size: 20px;
    font-weight: 600;
    color: #6c757d;
  }
`;

const NutritionInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 10px;
  margin: 16px 0;
  
  .nutri-badge {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    background: linear-gradient(145deg, #f8f9fa, #e9ecef);
    padding: 12px 8px;
    border-radius: 12px;
    text-align: center;
    transition: all 0.3s ease;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    border: 1px solid rgba(0,0,0,0.03);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    }
    
    svg {
      margin-bottom: 6px;
      color: #4361ee;
      background: rgba(67, 97, 238, 0.1);
      padding: 6px;
      border-radius: 50%;
    }
    
    .nutri-value {
      font-weight: 600;
      color: #2b2d42;
      margin: 2px 0;
    }
    
    .nutri-label {
      font-size: 11px;
      color: #6c757d;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }
`;

const IngredientList = styled.ul`
  padding: 0;
  margin: 12px 0;
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  
  li {
    margin: 0;
    padding: 10px 12px;
    background: #fff;
    border-radius: 8px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 4px rgba(0,0,0,0.03);
    border: 1px solid #f1f3f5;
    transition: all 0.2s ease;
    
    &:hover {
      transform: translateX(4px);
      border-color: #dee2e6;
      box-shadow: 0 4px 8px rgba(0,0,0,0.05);
    }
    
    .ingredient-name {
      flex: 1;
      font-weight: 500;
      color: #2b2d42;
    }
    
    .ingredient-quantity {
      background: #f8f9fa;
      color: #495057;
      font-size: 0.8em;
      padding: 2px 8px;
      border-radius: 10px;
      font-weight: 500;
      margin-left: 8px;
      white-space: nowrap;
    }
    
    .allergen-badge {
      background-color: #fff8e6;
      color: #8a6d3b;
      padding: 2px 8px;
      border-radius: 10px;
      font-size: 0.65em;
      margin-left: 8px;
      display: inline-flex;
      align-items: center;
      gap: 4px;
      font-weight: 500;
      border: 1px solid #ffeeba;
      transition: all 0.2s ease;
      
      &:hover {
        background-color: #fff3cd;
      }
      
      svg {
        width: 12px;
        height: 12px;
      }
    }
  }
`;

const EnhancedDietPlan = ({ dailyMeal, daily }) => {
  // États
  const [showDetails, setShowDetails] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isConsumed, setIsConsumed] = useState(dailyMeal?.consumed || false);
  const [showTooltip, setShowTooltip] = useState(false);
  const [showPastMealTooltip, setShowPastMealTooltip] = useState(false);
  
  // Données du repas
  const { recipe } = dailyMeal || {};
  console.log(' recipe in EnhancedDietPlan', recipe);
  const hasImage = recipe?.imageUrl && !recipe.imageUrl.includes('example.com');
  const mealName = recipe?.recipeName || 'Repas sans nom';
  const nutrition = recipe?.totalNV || {};
  const isPastMeal = daily?.dailyDate && moment(daily.dailyDate).isBefore(moment().startOf('day'));
  const isToday = daily?.dailyDate && moment(daily.dailyDate).isSame(moment(), 'day');
  
  const dispatch = useDispatch();

  // Grouper les ingrédients par catégorie
  const ingredientsByCategory = useMemo(() => {
    if (!recipe?.ingredients) return {};
    
    return recipe.ingredients.reduce((acc, ing) => {
      const category = ing.food?.category || 'Autres';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(ing);
      return acc;
    }, {});
  }, [recipe?.ingredients]);

  // Fonction pour enregistrer la consommation du repas
  const saveConsumeFood = async (e) => {
    e.stopPropagation();
    if (isLoading || isPastMeal) return;
    
    setIsLoading(true);
    try {
       const result = await dispatch(GetDailyDataDashUser({
                  dailyMealID: dailyMeal?.id,
                  dailyMealPlanId: daily?.id,
                }));
      setIsConsumed(true);
      toast.success('Repas marqué comme consommé avec succès');
    } catch (error) {
      console.error('Erreur lors de la consommation du repas:', error);
      toast.error('Erreur lors de la consommation du repas');
    } finally {
      setIsLoading(false);
    }
  };

  // Rendu du contenu du repas
  const renderMealContent = () => {
    if (!recipe) {
      return (
        <div className="text-center p-4">
          <p className="text-muted">Aucun repas planifié</p>
        </div>
      );
    }

    return (
      <StyledCard>
        <MealHeader 
        onClick={() => setShowDetails(!showDetails)}
        $isOpen={showDetails}
        >
       <MealTitle>
        <MealImage 
            $imageUrl={DEJEUNER} 
            $hasImage={true}
            className="meal-image ml-5 "
        >
            <img 
                src={DEJEUNER} 
                alt={mealName}
                style={{
                    width: '300%',
                    height: '150%',
                    objectFit: 'cover',
                    borderRadius: '8px'
                }}
            />
        </MealImage>
        <div className="d-flex flex-column ml-5">
            <div className="d-flex align-items-center">
                <span className="me-2">{mealName}</span>
                {isConsumed && !isPastMeal && (
                    <Badge bg="success" className="d-flex align-items-center">
                        <CheckCircle size={14} className="me-1" />
                        Consommé
                    </Badge>
                )}
            </div>
            {isPastMeal && !isConsumed && (
                <small className="text-muted">Non consommé</small>
            )}
        </div>
    </MealTitle>
        
        <div className="d-flex align-items-center">
            <NutritionBadge 
            bg={isPastMeal && !isConsumed ? "light" : "light"} 
            text={isPastMeal && !isConsumed ? "muted" : "dark"} 
            className="me-2"
            >
            <Zap size={14} className={`me-1 ${isPastMeal && !isConsumed ? "text-muted" : "text-warning"}`} />
            <span className={isPastMeal && !isConsumed ? "text-muted" : ""}>
                {Math.round(nutrition.calories || 0)} kcal
            </span>
            </NutritionBadge>
            {showDetails ? (
            <ChevronUp size={20} className="text-muted" />
            ) : (
            <ChevronDown size={20} className="text-muted" />
            )}
        </div>
        </MealHeader>

        <AnimatePresence>
          {showDetails && (
            <motion.div
              initial={{ opacity: 0, height: 'auto', overflow: 'hidden' }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="p-4">
                {/* Description et bouton d'action */}
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <div>
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-primary bg-opacity-10 p-2 rounded-circle me-2">
                        <Utensils size={20} className="text-primary" />
                      </div>
                      <h5 className="mb-0">Détails du repas</h5>
                    </div>
                    {recipe.description && (
                      <div className="bg-light p-3 rounded-3 mb-3">
                        <p className="mb-0 text-muted">
                          {recipe.description}
                        </p>
                      </div>
                    )}
                    
                    {nutrition && (
                      <NutritionInfo>
                        <div className="nutri-badge">
                          <Zap size={16} />
                          <div className="nutri-value">{Math.round(nutrition.calories || 0)}</div>
                          <div className="nutri-label">Calories</div>
                        </div>
                        <div className="nutri-badge">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17.5 2.9c-2.1 0-4.1 1.3-5.4 2.8C10.5 3.8 7.9 3 5.1 3.1 3.3 3.1.7 6.5 3 14.1c.9 2.7 3.8 8.3 6.9 6.5 2.7-1.5 2.8-1.1 4.9-1.1s2.2-.4 4.9 1.1c3.1 1.9 6-3.8 6.9-6.5 1.9-5.7-.9-10.5-2.8-11.4-.6-.3-1.5-.4-2.3-.4z"></path>
                          </svg>
                          <div className="nutri-value">{Math.round(nutrition.proteins || 0)}g</div>
                          <div className="nutri-label">Protéines</div>
                        </div>
                        <div className="nutri-badge">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 3h18v18H3z"></path>
                            <path d="M3 9h18M3 15h18M9 3v18M15 3v18"></path>
                          </svg>
                          <div className="nutri-value">{Math.round(nutrition.carbs || 0)}g</div>
                          <div className="nutri-label">Glucides</div>
                        </div>
                        <div className="nutri-badge">
                          <Droplet size={16} />
                          <div className="nutri-value">{Math.round(nutrition.fats || 0)}g</div>
                          <div className="nutri-label">Lipides</div>
                        </div>
                      </NutritionInfo>
                    )}
                  </div>
                  
                  <OverlayTrigger
                    placement="top"
                    show={showPastMealTooltip}
                    overlay={
                      <Tooltip id="past-meal-tooltip">
                        Vous ne pouvez pas consommer un repas d'une date passée
                      </Tooltip>
                    }
                  >
                    <div className="d-inline-block">
                      <Button
                        variant={isPastMeal ? 'outline-secondary' : isConsumed ? 'outline-success' : 'primary'}
                        size="sm"
                        onClick={isPastMeal ? () => setShowPastMealTooltip(true) : saveConsumeFood}
                        disabled={isLoading || isConsumed || isPastMeal}
                        onMouseLeave={() => setShowPastMealTooltip(false)}
                        className="d-flex align-items-center gap-1"
                      >
                        {isLoading ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
                            En cours...
                          </>
                        ) : isConsumed ? (
                          <>
                            <CheckCircle size={16} className="me-1" />
                            Déjà consommé
                          </>
                        ) : isPastMeal ? (
                          'Date passée'
                        ) : (
                          'Marquer comme consommé'
                        )}
                      </Button>
                    </div>
                  </OverlayTrigger>
                </div>
                
                {/* Ingrédients */}
                {Object.keys(ingredientsByCategory).length > 0 && (
                  <div className="mt-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-success bg-opacity-10 p-2 rounded-circle me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-success">
                          <path d="M6 2L3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z"></path>
                          <line x1="3" y1="6" x2="21" y2="6"></line>
                          <path d="M16 10a4 4 0 0 1-8 0"></path>
                        </svg>
                      </div>
                      <h5 className="mb-0">Ingrédients</h5>
                    </div>
                    {Object.entries(ingredientsByCategory).map(([category, ingredients]) => (
                      <div key={category} className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <span className="badge bg-light text-dark fw-normal px-3 py-1">
                            {category}
                          </span>
                        </div>
                        <IngredientList>
                          {ingredients.map((ingredient, idx) => {
                            const hasAllergens = ingredient.food?.allergens?.length > 0;
                            return (
                              <li key={idx}>
                                <div>
                                  {ingredient.ingredientName || ingredient.food?.name}
                                  {hasAllergens && (
                                    <OverlayTrigger
                                      placement="top"
                                      overlay={
                                        <Tooltip>
                                          Allergènes: {ingredient.food.allergens.map(a => a.name).join(', ')}
                                        </Tooltip>
                                      }
                                    >
                                      <span className="allergen-badge">
                                        <AlertCircle size={12} />
                                        Allergènes
                                      </span>
                                    </OverlayTrigger>
                                  )}
                                </div>
                                <span className="ingredient-quantity">
                                  {ingredient.quantity} {ingredient.unit}
                                </span>
                              </li>
                            );
                          })}
                        </IngredientList>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Instructions de préparation */}
                {recipe.instructions && (
                  <div className="mt-4">
                    <div className="d-flex align-items-center mb-3">
                      <div className="bg-info bg-opacity-10 p-2 rounded-circle me-2">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-info">
                          <circle cx="12" cy="12" r="10"></circle>
                          <line x1="12" y1="16" x2="12" y2="12"></line>
                          <line x1="12" y1="8" x2="12.01" y2="8"></line>
                        </svg>
                      </div>
                      <h5 className="mb-0">Instructions de préparation</h5>
                    </div>
                    <div className="bg-light p-4 rounded-3">
                      <ol className="mb-0 ps-3" style={{ listStyleType: 'none', counterReset: 'step-counter' }}>
                        {recipe.instructions.split('\n').filter(p => p.trim()).map((paragraph, idx) => (
                          <li key={idx} className="position-relative ps-4 mb-3" style={{ counterIncrement: 'step-counter' }}>
                            <div className="position-absolute start-0 top-0 bg-primary text-white rounded-circle d-flex align-items-center justify-content-center" style={{ width: '24px', height: '24px', fontSize: '12px' }}>
                              {idx + 1}
                            </div>
                            <div className="ms-4 ps-2">
                              <p className="mb-0">
                                {paragraph.trim()}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ol>
                    </div>
                  </div>
                )}
                
                {/* Lien vidéo */}
                {recipe.videoUrl && !recipe.videoUrl.includes('example.com') && (
                  <div className="mt-4">
                    <a 
                      href={recipe.videoUrl} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="btn btn-primary d-inline-flex align-items-center"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="me-2">
                        <polygon points="23 7 16 12 23 17 23 7"></polygon>
                        <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                      </svg>
                      Voir la vidéo de préparation
                    </a>
                  </div>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </StyledCard>
    );
  };

  return renderMealContent();
};

export default EnhancedDietPlan;